import { useState, useEffect, useCallback, useMemo } from 'react';
import type { FC, ReactNode } from 'react';
import {
  Input, InputNumber, Select, Checkbox, Switch,
  message, Spin, Tooltip
} from 'antd';
import {
  PlusOutlined,
  CloseOutlined,
  HolderOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { MilestoneTemplate, MilestoneNodeConfig, MilestoneTimeMethod, TemplateMilestoneConfig } from '../types';
import { milestoneNodeApi, nodeAutoCompleteRuleApi, permissionConfigApi, templateApi } from '../api/services';
import type { MilestoneNodeVO, MilestoneTemplateVO, TemplateMilestoneVO, TemplateOwnerVO, UserOptionVO } from '../api/types';
import { mapNodeAutoCompleteRuleVO } from '../api/types/nodeAutoCompleteRule';
import { hasEditableOrSystemAdminPermission, hasViewableOrHigherPermission } from '../utils/permissionCodes';
import { useNavigate, useParams } from 'react-router-dom';
import { IpcButton, IpcEmpty, IpcFilterToolbar, IpcFormField, IpcModal, IpcModuleTabs, IpcRowActions, IpcTable, IpcTag, ipcConfirm, IPC_FILTER_MULTI_SELECT_PROPS } from '../components/ui';
import {
  milestonePhaseOptions,
  nodeCompletionMethodLabel,
  nodeCompletionMethodOptions,
} from '../utils/ipcStatusEnums';
import { useIpcStatusEnums } from '../utils/useIpcStatusEnums';
import { useListViewQuery } from '../hooks/useListViewQuery';
import { LIST_VIEW_KEYS } from '../utils/listViewSession';
import NodeAutoCompleteRulesTab from './NodeAutoCompleteRulesTab';
import type { NodeAutoCompleteRule } from '../types/nodeAutoCompleteRule';
import './MilestoneConfig.css';

type TemplateDependencyDirection = 'parent' | 'next';
type TemplateMilestoneStatus = 'manual' | 'configured' | 'pending';

/** 将后端节点 VO 转为页面使用的节点配置（兼容 snake_case / camelCase，展示以前端为准） */
function nodeVOToConfig(vo: MilestoneNodeVO): MilestoneNodeConfig {
  const completionRaw = (vo.completion_method ?? vo.completionMethod ?? 'manual').toString().trim().toLowerCase();
  const completionMethod: MilestoneNodeConfig['completionMethod'] =
    completionRaw === 'auto' ? 'auto' : 'manual';
  const finalRaw = vo.is_final_node ?? vo.isFinalNode;
  const isFinalNode = finalRaw === true || finalRaw === 1 || String(finalRaw) === '1';
  return {
    id: String(vo.id),
    milestoneId: vo.milestone_code ?? vo.milestoneCode ?? '',
    name: vo.name ?? '',
    phase: vo.phase ?? '',
    defaultLeadDays: vo.default_lead_days ?? vo.defaultLeadDays ?? 0,
    defaultManDays: Number(vo.default_man_days ?? vo.defaultManDays ?? 0),
    defaultCost: Number(vo.default_cost ?? vo.defaultCost ?? 0),
    completionMethod,
    isFinalNode,
    autoCompleteRuleId: (vo.auto_complete_rule_id ?? vo.autoCompleteRuleId) != null
      ? String(vo.auto_complete_rule_id ?? vo.autoCompleteRuleId)
      : undefined,
    description: vo.description ?? undefined,
    type: (vo.type as MilestoneNodeConfig['type']) ?? undefined,
  };
}

/** 从模板节点 VO 取字段（兼容 snake_case / camelCase，展示以前端为准） */
function getTmDisplay(tm: TemplateMilestoneVO) {
  return {
    milestoneId: tm.milestone_id ?? tm.milestoneId ?? 0,
    milestoneName: tm.milestone_name ?? tm.milestoneName ?? '-',
    milestoneOrder: tm.milestone_order ?? tm.milestoneOrder ?? 0,
    timeMethod: tm.time_method ?? tm.timeMethod ?? 'manual',
    mergeAcrossSpecs: tm.merge_across_specs ?? tm.mergeAcrossSpecs ?? false,
    parentMilestoneId: tm.parent_milestone_id ?? tm.parentMilestoneId ?? null,
    nextMilestoneId: tm.next_milestone_id ?? tm.nextMilestoneId ?? null,
    durationDays: tm.duration_days ?? tm.durationDays ?? null,
    timeOffsetDays: tm.time_offset_days ?? tm.timeOffsetDays ?? 1,
    excludeWeekends: tm.exclude_weekends ?? tm.excludeWeekends ?? false,
  };
}

function getOwnerUserId(owner: TemplateOwnerVO): number {
  return owner.user_id ?? owner.userId ?? 0;
}

function getOwnerUserName(owner: TemplateOwnerVO): string {
  return owner.user_name ?? owner.userName ?? '';
}

function getTemplateOwners(template: MilestoneTemplateVO): TemplateOwnerVO[] {
  return template.owners ?? [];
}

function templateUsesOwnerPermission(template: MilestoneTemplateVO): boolean {
  if (template.has_owners === true) return true;
  if (template.has_owners === false) return false;
  return getTemplateOwners(template).length > 0;
}

function getOwnerInitial(name: string): string {
  const trimmed = name.trim();
  return trimmed ? trimmed.charAt(0) : '?';
}

function isTemplateOwnerFlag(template: MilestoneTemplateVO): boolean {
  return template.is_current_user_owner === true || template.isCurrentUserOwner === true;
}

function ownerFromUser(user: UserOptionVO): TemplateOwnerVO {
  return { user_id: user.id, user_name: user.user_name };
}

function getTemplateNodeCount(template: MilestoneTemplateVO): number {
  if (template.milestones) return template.milestones.length;
  return template.milestone_count ?? template.milestoneCount ?? 0;
}

function formatConfigDate(value?: string): string {
  if (!value) return '-';
  const parsed = new Date(value.includes('T') ? value : value.replace(' ', 'T'));
  if (Number.isNaN(parsed.getTime())) return value.slice(0, 10);
  return parsed.toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).replace(/\//g, '-');
}

/** 将后端模板详情 VO 转为页面使用的模板（含 milestones），兼容 snake_case / camelCase */
function templateDetailToForm(vo: MilestoneTemplateVO): { form: Partial<MilestoneTemplate>; milestones: TemplateMilestoneConfig[] } {
  const list = vo.milestones ?? [];
  const milestones: TemplateMilestoneConfig[] = list.map(m => {
    const d = getTmDisplay(m);
    return {
      milestoneId: d.milestoneId,
      order: d.milestoneOrder,
      timeMethod: d.timeMethod as MilestoneTimeMethod,
      mergeAcrossSpecs: d.mergeAcrossSpecs,
      parentMilestoneId: d.parentMilestoneId ?? undefined,
      nextMilestoneId: d.nextMilestoneId ?? undefined,
      durationDays: d.durationDays ?? undefined,
      timeOffsetDays: d.timeOffsetDays ?? 1,
      excludeWeekends: d.excludeWeekends,
    };
  });
  return {
    form: { id: vo.id, name: vo.name ?? '', description: vo.description ?? '' },
    milestones,
  };
}

const { TextArea } = Input;

const isReq022Preview = ((import.meta as any).env?.BASE_URL || '').startsWith('/REQ-022/');
const MILESTONE_TABLE_PAGE_SIZE_OPTIONS = [20, 50, 100];

const previewUsers: UserOptionVO[] = [
  { id: 9001, user_name: '高茜' },
  { id: 9002, user_name: '史绒绒' },
  { id: 9003, user_name: '张日文' },
];

const previewTemplates: MilestoneTemplateVO[] = [
  {
    id: 2201,
    name: '护肤新品标准排期模板',
    description: 'CP3 预览数据：用于验证 REQ-022 模板名称筛选与 Owner 筛选。',
    has_owners: true,
    is_current_user_owner: true,
    owners: [
      { user_id: 9001, user_name: '高茜' },
      { user_id: 9003, user_name: '张日文' },
    ],
    milestones: [
      { milestone_id: 1001, milestone_name: '需求确认', milestone_order: 1, time_method: 'manual', merge_across_specs: true },
      { milestone_id: 1002, milestone_name: '配方打样', milestone_order: 2, time_method: 'auto', parent_milestone_id: 1001, duration_days: 5, exclude_weekends: true },
      { milestone_id: 1003, milestone_name: '包材确认', milestone_order: 3, time_method: 'auto', parent_milestone_id: 1002, duration_days: 7 },
      { milestone_id: 1004, milestone_name: '上市评审', milestone_order: 4, time_method: 'manual' },
    ],
  },
  {
    id: 2202,
    name: '彩妆快反排期模板',
    description: 'CP3 预览数据：Owner 候选仅保留有效可编辑用户。',
    has_owners: true,
    is_current_user_owner: true,
    owners: [
      { user_id: 9002, user_name: '史绒绒' },
    ],
    milestones: [
      { milestone_id: 1011, milestone_name: '色号确认', milestone_order: 1, time_method: 'manual' },
      { milestone_id: 1012, milestone_name: '样品测试', milestone_order: 2, time_method: 'auto', parent_milestone_id: 1011, duration_days: 3 },
      { milestone_id: 1013, milestone_name: '投产准备', milestone_order: 3, time_method: 'auto', parent_milestone_id: 1012, duration_days: 4, merge_across_specs: true },
    ],
  },
];

const previewNodeConfigs: MilestoneNodeConfig[] = [
  { id: '1001', milestoneId: 'REQ', name: '需求确认', phase: '立项', defaultLeadDays: 1 },
  { id: '1002', milestoneId: 'FORMULA', name: '配方打样', phase: '配方', defaultLeadDays: 5 },
  { id: '1003', milestoneId: 'PACKAGE', name: '包材确认', phase: '包材', defaultLeadDays: 7 },
  { id: '1004', milestoneId: 'REVIEW', name: '上市评审', phase: '生产', defaultLeadDays: 1 },
];

const MILESTONE_CONFIG_TABS = ['templates', 'nodes', 'autoCompleteRules'] as const;
type MilestoneConfigTabKey = (typeof MILESTONE_CONFIG_TABS)[number];

function isMilestoneConfigTabKey(value: string | undefined): value is MilestoneConfigTabKey {
  return value != null && (MILESTONE_CONFIG_TABS as readonly string[]).includes(value);
}

const MilestoneConfig: FC = () => {
  const { ready: enumsReady } = useIpcStatusEnums();
  const navigate = useNavigate();
  const { tab: tabParam } = useParams<{ tab?: string }>();
  const activeTab: MilestoneConfigTabKey = isMilestoneConfigTabKey(tabParam) ? tabParam : 'templates';

  useEffect(() => {
    if (!isMilestoneConfigTabKey(tabParam)) {
      navigate('/config/templates', { replace: true });
    }
  }, [tabParam, navigate]);

  const [canViewMilestone, setCanViewMilestone] = useState(false);
  const [canMutateMilestone, setCanMutateMilestone] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const [currentUserName, setCurrentUserName] = useState('');

  // Owner 选择器
  const [showOwnerSelector, setShowOwnerSelector] = useState(false);
  const [ownerSelectorTarget, setOwnerSelectorTarget] = useState<'list' | 'edit'>('list');
  const [ownerSelectorTemplateId, setOwnerSelectorTemplateId] = useState<number | null>(null);
  const [ownerSearchKeyword, setOwnerSearchKeyword] = useState('');
  const [debouncedOwnerKeyword, setDebouncedOwnerKeyword] = useState('');
  const [allUsers, setAllUsers] = useState<UserOptionVO[]>([]);
  const [pendingOwnerIds, setPendingOwnerIds] = useState<number[]>([]);
  const [ownerSaveLoading, setOwnerSaveLoading] = useState(false);
  const [editFormOwners, setEditFormOwners] = useState<TemplateOwnerVO[]>([]);

  // 里程碑节点配置（来自接口）
  const [nodeConfigs, setNodeConfigs] = useState<MilestoneNodeConfig[]>([]);
  const [nodeEditForm, setNodeEditForm] = useState<Partial<MilestoneNodeConfig>>({});
  const [isAddingNode, setIsAddingNode] = useState(false);
  const [showNodeModal, setShowNodeModal] = useState(false);
  const [nodesLoading, setNodesLoading] = useState(false);
  const [nodeSaveLoading, setNodeSaveLoading] = useState(false);

  /** 自动完成规则列表（节点下拉与规则 Tab 共用） */
  const [autoCompleteRules, setAutoCompleteRules] = useState<NodeAutoCompleteRule[]>([]);
  const [autoCompleteRulesLoading, setAutoCompleteRulesLoading] = useState(false);
  const autoCompleteRuleOptions = useMemo(
    () => autoCompleteRules.map(r => ({ value: r.id, label: r.name })),
    [autoCompleteRules],
  );

  const loadAutoCompleteRules = useCallback(async () => {
    setAutoCompleteRulesLoading(true);
    try {
      const res = await nodeAutoCompleteRuleApi.getList({ pageNum: 1, pageSize: 9999 });
      setAutoCompleteRules((res.list ?? []).map(mapNodeAutoCompleteRuleVO));
    } catch (e) {
      console.error('加载自动完成规则失败', e);
      message.error('加载自动完成规则失败');
      setAutoCompleteRules([]);
    } finally {
      setAutoCompleteRulesLoading(false);
    }
  }, []);

  // 里程碑模板（对接接口）
  const [templates, setTemplates] = useState<MilestoneTemplateVO[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState<number | null>(null);
  const [templateEditForm, setTemplateEditForm] = useState<Partial<MilestoneTemplate>>({});
  const [templateMilestones, setTemplateMilestones] = useState<TemplateMilestoneConfig[]>([]);
  const [templateRelationDirectionDrafts, setTemplateRelationDirectionDrafts] = useState<Record<number, TemplateDependencyDirection>>({});
  const [templateDragState, setTemplateDragState] = useState<{ dragId: number | null; overId: number | null }>({
    dragId: null,
    overId: null,
  });
  const [isAddingTemplate, setIsAddingTemplate] = useState(false);
  const [showMilestoneSelector, setShowMilestoneSelector] = useState(false);
  const [tempSelectedMilestoneIds, setTempSelectedMilestoneIds] = useState<number[]>([]);
  const [templateSaveLoading, setTemplateSaveLoading] = useState(false);

  // REQ-022: 筛选状态 — 里程碑名字 + Owner（多选）
  type TemplateFilters = { filterName: string; filterOwnerIds: number[] };
  const DEFAULT_TEMPLATE_FILTERS: TemplateFilters = { filterName: '', filterOwnerIds: [] };
  const {
    draft: templateDraft,
    patchDraft: patchTemplateDraft,
    applied: templateApplied,
    applyIfChanged: applyTemplateIfChanged,
    patchAndApply: patchAndApplyTemplate,
    reset: resetTemplateQuery,
    patchApplied: patchTemplateApplied,
    page: templatePage,
    pageSize: templatePageSize,
    setPagination: setTemplatePagination,
  } = useListViewQuery<TemplateFilters>({
    listViewKey: LIST_VIEW_KEYS.milestoneTemplates,
    defaultFilters: DEFAULT_TEMPLATE_FILTERS,
    emptyFilters: DEFAULT_TEMPLATE_FILTERS,
  });
  const filterName = templateDraft.filterName;
  const filterOwnerIds = templateDraft.filterOwnerIds;
  const appliedFilterName = templateApplied.filterName;
  const appliedFilterOwnerIds = templateApplied.filterOwnerIds;

  type NodeFilters = {
    nodeFilterName: string;
    nodeFilterPhases: string[];
    nodeFilterCompletionMethods: string[];
    nodeFilterFinalNode: string;
  };
  const DEFAULT_NODE_FILTERS: NodeFilters = {
    nodeFilterName: '',
    nodeFilterPhases: [],
    nodeFilterCompletionMethods: [],
    nodeFilterFinalNode: '',
  };
  const {
    draft: nodeDraft,
    patchDraft: patchNodeDraft,
    applied: nodeApplied,
    applyIfChanged: applyNodeIfChanged,
    patchAndApply: patchAndApplyNode,
    reset: resetNodeQuery,
    patchApplied: patchNodeApplied,
    page: nodePage,
    pageSize: nodePageSize,
    setPagination: setNodePagination,
  } = useListViewQuery<NodeFilters>({
    listViewKey: LIST_VIEW_KEYS.milestoneNodes,
    defaultFilters: DEFAULT_NODE_FILTERS,
    emptyFilters: DEFAULT_NODE_FILTERS,
  });
  const nodeFilterName = nodeDraft.nodeFilterName;
  const appliedNodeFilterName = nodeApplied.nodeFilterName;
  const nodeFilterPhases = nodeDraft.nodeFilterPhases;
  const appliedNodeFilterPhases = nodeApplied.nodeFilterPhases;
  const nodeFilterCompletionMethods = nodeDraft.nodeFilterCompletionMethods;
  const appliedNodeFilterCompletionMethods = nodeApplied.nodeFilterCompletionMethods;
  /** '' = 全部；'1' = 是；'0' = 否 */
  const nodeFilterFinalNode = nodeDraft.nodeFilterFinalNode;
  const appliedNodeFilterFinalNode = nodeApplied.nodeFilterFinalNode;

  /** 从接口加载节点配置 */
  const loadNodeConfigs = useCallback(async () => {
    setNodesLoading(true);
    try {
      const res = await milestoneNodeApi.getList({ pageNum: 1, pageSize: 9999 });
      setNodeConfigs((res.list ?? []).map(nodeVOToConfig));
    } catch (e) {
      console.error('加载节点配置失败', e);
      if (isReq022Preview) {
        setNodeConfigs(previewNodeConfigs);
        return;
      }
      message.error('加载节点配置失败，请检查网络或后端服务');
      setNodeConfigs([]);
    } finally { 
      setNodesLoading(false);
    }
  }, []);

  /** 从接口加载模板列表（IncludeDetail 一次带出 milestones，避免 N 次 detail） */
  const loadTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    try {
      const res = await templateApi.getList({ pageNum: 1, pageSize: 9999, includeDetail: true });
      setTemplates(res.list ?? []);
    } catch (e) {
      console.error('加载模板列表失败', e);
      if (isReq022Preview) {
        setTemplates(previewTemplates);
        return;
      }
      message.error('加载模板列表失败');
      setTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
    loadNodeConfigs();
    loadAutoCompleteRules();
  }, [loadTemplates, loadNodeConfigs, loadAutoCompleteRules]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [codes, me] = await Promise.all([
          permissionConfigApi.getMyPermissionCodes(),
          permissionConfigApi.getMe(),
        ]);
        if (!cancelled) {
          setCanViewMilestone(isReq022Preview || hasViewableOrHigherPermission(codes));
          setCanMutateMilestone(isReq022Preview || hasEditableOrSystemAdminPermission(codes));
          setCurrentUserId(me?.id ?? (isReq022Preview ? 9001 : null));
          setCurrentUserName(me?.user_name ?? (isReq022Preview ? '高茜' : ''));
        }
      } catch (e) {
        console.error('加载里程碑操作权限失败', e);
        if (!cancelled) {
          setCanViewMilestone(isReq022Preview);
          setCanMutateMilestone(isReq022Preview);
          setCurrentUserId(isReq022Preview ? 9001 : null);
          setCurrentUserName(isReq022Preview ? '高茜' : '');
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedOwnerKeyword(ownerSearchKeyword.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [ownerSearchKeyword]);

  /** Owner 模板仅 Owner 可改；无 Owner 的历史模板走全局可编辑/系统管理权限 */
  const canMutateTemplate = useCallback((template: MilestoneTemplateVO): boolean => {
    if (!canMutateMilestone) return false;
    if (!templateUsesOwnerPermission(template)) return true;
    return isTemplateOwnerFlag(template);
  }, [canMutateMilestone]);

  const editingTemplate = useMemo(
    () => (editingTemplateId != null ? templates.find(t => t.id === editingTemplateId) : undefined),
    [editingTemplateId, templates]
  );

  const canEditCurrentTemplate = isAddingTemplate
    || (editingTemplate != null && canMutateTemplate(editingTemplate));

  const showTemplateEditForm = canEditCurrentTemplate && (isAddingTemplate || editingTemplateId != null);

  // REQ-022: 根据筛选条件过滤模板列表
  const filteredTemplates = useMemo(() => {
    let list = templates;
    const kw = appliedFilterName.trim().toLowerCase();
    if (kw) {
      list = list.filter(t => (t.name ?? '').toLowerCase().includes(kw));
    }
    if (appliedFilterOwnerIds.length > 0) {
      const selectedIds = new Set(appliedFilterOwnerIds);
      list = list.filter(t => {
        const owners = getTemplateOwners(t);
        return owners.some(o => selectedIds.has(getOwnerUserId(o)));
      });
    }
    return list;
  }, [templates, appliedFilterName, appliedFilterOwnerIds]);

  const templateTotalPages = Math.max(1, Math.ceil(filteredTemplates.length / templatePageSize) || 1);
  const safeTemplatePage = Math.min(Math.max(1, templatePage), templateTotalPages);

  useEffect(() => {
    if (filteredTemplates.length === 0) return;
    if (templatePage > templateTotalPages) {
      setTemplatePagination(templateTotalPages, templatePageSize);
    }
  }, [filteredTemplates.length, templatePage, templatePageSize, templateTotalPages, setTemplatePagination]);
  const pagedTemplates = filteredTemplates.slice(
    (safeTemplatePage - 1) * templatePageSize,
    safeTemplatePage * templatePageSize,
  );

  const ownerFilterOptions = useMemo(
    () => [...allUsers]
      .map(u => ({ value: u.id, label: u.user_name }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [allUsers]
  );

  const existingOwnerIds = useMemo(() => {
    if (ownerSelectorTarget === 'edit') {
      return new Set(editFormOwners.map(getOwnerUserId));
    }
    if (ownerSelectorTemplateId != null) {
      const template = templates.find(t => t.id === ownerSelectorTemplateId);
      return new Set(getTemplateOwners(template ?? { id: 0, owners: [] }).map(getOwnerUserId));
    }
    return new Set<number>();
  }, [editFormOwners, ownerSelectorTarget, ownerSelectorTemplateId, templates]);

  const filteredOwnerCandidates = useMemo(() => {
    const keyword = debouncedOwnerKeyword.toLowerCase();
    return allUsers.filter(u => {
      if (!keyword) return true;
      return u.user_name.toLowerCase().includes(keyword);
    });
  }, [allUsers, debouncedOwnerKeyword]);

  const loadAllUsers = useCallback(async () => {
    if (allUsers.length > 0) return;
    try {
      const users = await permissionConfigApi.getMutableUsers();
      setAllUsers(users ?? []);
    } catch (e) {
      console.error('加载可编辑用户列表失败', e);
      if (isReq022Preview) {
        setAllUsers(previewUsers);
        return;
      }
      message.error('加载用户列表失败');
    }
  }, [allUsers.length]);

  // Owner 筛选下拉与「新增 Owner」共用可编辑用户列表
  useEffect(() => {
    loadAllUsers();
  }, [loadAllUsers]);

  const openOwnerSelector = async (target: 'list' | 'edit', template?: MilestoneTemplateVO) => {
    await loadAllUsers();
    setOwnerSelectorTarget(target);
    setOwnerSelectorTemplateId(template?.id ?? null);
    setOwnerSearchKeyword('');
    setDebouncedOwnerKeyword('');
    setPendingOwnerIds([]);
    setShowOwnerSelector(true);
  };

  const handleConfirmAddOwners = async () => {
    if (pendingOwnerIds.length === 0) {
      message.warning('请选择要添加的用户');
      return;
    }

    const selectedUsers = pendingOwnerIds
      .filter(id => !existingOwnerIds.has(id))
      .map(id => allUsers.find(u => u.id === id))
      .filter((u): u is UserOptionVO => u != null)
      .map(ownerFromUser);

    if (ownerSelectorTarget === 'edit') {
      const merged = [...editFormOwners];
      selectedUsers.forEach(u => {
        if (!merged.some(o => getOwnerUserId(o) === getOwnerUserId(u))) merged.push(u);
      });
      setEditFormOwners(merged);
      setShowOwnerSelector(false);
      return;
    }

    const templateId = ownerSelectorTemplateId;
    if (templateId == null) return;
    const template = templates.find(t => t.id === templateId);
    if (!template || !canMutateTemplate(template)) return;

    const nextIds = [...getTemplateOwners(template).map(getOwnerUserId)];
    pendingOwnerIds.forEach(id => {
      if (!nextIds.includes(id)) nextIds.push(id);
    });

    setOwnerSaveLoading(true);
    try {
      await templateApi.updateOwners(templateId, { ownerUserIds: nextIds });
      message.success('添加 Owner 成功');
      setShowOwnerSelector(false);
      await loadTemplates();
    } catch (e) {
      console.error('添加 Owner 失败', e);
      message.error(e instanceof Error ? e.message : '添加 Owner 失败');
    } finally {
      setOwnerSaveLoading(false);
    }
  };

  const handleRemoveOwnerFromList = async (template: MilestoneTemplateVO, userId: number) => {
    if (!canMutateTemplate(template)) return;
    const nextIds = getTemplateOwners(template).map(getOwnerUserId).filter(id => id !== userId);
    if (nextIds.length === 0) {
      message.warning('至少保留一位 Owner');
      return;
    }
    try {
      await templateApi.updateOwners(template.id, { ownerUserIds: nextIds });
      message.success('已移除 Owner');
      await loadTemplates();
    } catch (e) {
      console.error('移除 Owner 失败', e);
      message.error(e instanceof Error ? e.message : '移除 Owner 失败');
    }
  };

  const handleRemoveOwnerFromEdit = (userId: number) => {
    if (currentUserId != null && userId === currentUserId) return;
    const next = editFormOwners.filter(o => getOwnerUserId(o) !== userId);
    if (next.length === 0) {
      message.warning('至少保留一位 Owner');
      return;
    }
    setEditFormOwners(next);
  };

  const renderOwnerTag = (
    owner: TemplateOwnerVO,
    options: { removable: boolean; onRemove?: () => void }
  ) => {
    const userId = getOwnerUserId(owner);
    const userName = getOwnerUserName(owner);
    const isSelf = currentUserId != null && userId === currentUserId;
    const canRemove = options.removable && !isSelf;
    return (
      <span key={userId} className="owner-tag">
        <span className="owner-avatar">{getOwnerInitial(userName)}</span>
        <span className="owner-name">{userName}{isSelf ? '（你）' : ''}</span>
        {canRemove && (
          <button type="button" className="owner-remove-btn" onClick={options.onRemove} aria-label="移除">
            <CloseOutlined style={{ fontSize: 10 }} />
          </button>
        )}
      </span>
    );
  };

  const renderOwnerRow = (
    owners: TemplateOwnerVO[],
    options: { showAdd: boolean; onAdd?: () => void; removable: boolean; onRemove?: (userId: number) => void; hideLabel?: boolean }
  ) => (
    <div className="owner-row">
      {!options.hideLabel && <span className="owner-label">Owner</span>}
      <div className="owner-tags">
        {owners.length > 0
          ? owners.map(owner => renderOwnerTag(owner, {
            removable: options.removable,
            onRemove: options.onRemove ? () => options.onRemove!(getOwnerUserId(owner)) : undefined,
          }))
          : (!options.showAdd && <span className="owner-empty">-</span>)}
      </div>
      {options.showAdd && (
        <button type="button" className="add-owner-btn" onClick={options.onAdd}>+ 添加</button>
      )}
    </div>
  );

  const renderMutateButton = (
    template: MilestoneTemplateVO,
    button: ReactNode,
    disabledTitle = '仅 owner 可操作'
  ) => {
    const allowed = canMutateTemplate(template);
    if (!canMutateMilestone) return null;
    if (allowed) return button;
    return (
      <Tooltip title={disabledTitle}>
        <span className="disabled-action-wrap">{button}</span>
      </Tooltip>
    );
  };

  // ==================== 模板管理（对接接口） ====================

  const handleAddTemplate = () => {
    if (!canMutateMilestone) return;
    setTemplateEditForm({ name: '', description: '' });
    setTemplateMilestones([]);
    setEditFormOwners(currentUserId != null ? [{ user_id: currentUserId, user_name: currentUserName }] : []);
    setIsAddingTemplate(true);
    setEditingTemplateId(null);
    setTempSelectedMilestoneIds([]);
    setTemplateRelationDirectionDrafts({});
    setTemplateDragState({ dragId: null, overId: null });
  };

  const handleEditTemplate = (template: MilestoneTemplateVO) => {
    if (!canMutateTemplate(template)) return;
    const { form, milestones } = templateDetailToForm(template);
    setTemplateEditForm(form);
    setTemplateMilestones(milestones);
    setEditFormOwners(getTemplateOwners(template));
    setTempSelectedMilestoneIds(milestones.map(m => Number(m.milestoneId)));
    setEditingTemplateId(template.id);
    setIsAddingTemplate(false);
    setTemplateRelationDirectionDrafts({});
    setTemplateDragState({ dragId: null, overId: null });
  };

  const handleOpenMilestoneSelector = () => {
    if (!canEditCurrentTemplate) return;
    setTempSelectedMilestoneIds(templateMilestones.map(tm => Number(tm.milestoneId)));
    setShowMilestoneSelector(true);
  };

  const handleSaveMilestoneSelection = () => {
    if (!canEditCurrentTemplate) return;
    const ids = tempSelectedMilestoneIds;
    let updated = templateMilestones.filter(tm => ids.includes(Number(tm.milestoneId)));
    ids.forEach(milestoneId => {
      if (!updated.some(tm => Number(tm.milestoneId) === milestoneId)) {
        updated.push({ milestoneId, timeMethod: 'manual' as const, mergeAcrossSpecs: false, order: updated.length + 1, timeOffsetDays: 1 });
      }
    });
    updated = updated.map((m, i) => ({ ...m, order: i + 1 }));
    setTemplateMilestones(updated);
    setShowMilestoneSelector(false);
  };

  const handleSaveTemplate = async () => {
    if (!canEditCurrentTemplate) return;
    if (!isAddingTemplate && editingTemplate && !canMutateTemplate(editingTemplate)) return;
    const name = templateEditForm.name?.trim();
    if (!name) {
      message.warning('请填写模板名称');
      return;
    }
    const invalidMilestone = templateMilestones.find(m => getTemplateMilestoneStatus(m).status === 'pending');
    if (invalidMilestone) {
      const nodeName = nodeConfigs.find(n => Number(n.id) === Number(invalidMilestone.milestoneId))?.name ?? `节点${invalidMilestone.milestoneId}`;
      const state = getTemplateMilestoneStatus(invalidMilestone);
      message.warning(`${nodeName}：${state.message ?? '配置待完善'}`);
      return;
    }
    const milestonesPayload = templateMilestones.map((m, i) => ({
      milestoneId: Number(m.milestoneId),
      milestoneOrder: i + 1,
      timeMethod: m.timeMethod,
      mergeAcrossSpecs: m.mergeAcrossSpecs ?? false,
      parentMilestoneId: m.parentMilestoneId != null ? Number(m.parentMilestoneId) : null,
      nextMilestoneId: m.nextMilestoneId != null ? Number(m.nextMilestoneId) : null,
      durationDays: m.durationDays ?? null,
      timeOffsetDays: m.timeOffsetDays ?? 1,
      excludeWeekends: m.excludeWeekends ?? false,
    }));
    const ownerUserIds = editFormOwners.map(getOwnerUserId).filter(id => id > 0);
    setTemplateSaveLoading(true);
    try {
      if (isAddingTemplate) {
        await templateApi.create({
          name,
          description: templateEditForm.description?.trim() || undefined,
          ownerUserIds,
          milestones: milestonesPayload,
        });
        message.success('新建成功');
        await loadTemplates();
        setEditingTemplateId(null);
        setTemplateEditForm({});
        setTemplateMilestones([]);
        setEditFormOwners([]);
        setIsAddingTemplate(false);
        setTempSelectedMilestoneIds([]);
        setTemplateRelationDirectionDrafts({});
        setTemplateDragState({ dragId: null, overId: null });
      } else {
        const id = editingTemplateId;
        if (id == null) return;
        const editingTemplate = templates.find(t => t.id === id);
        if (editingTemplate && !canMutateTemplate(editingTemplate)) return;
        await templateApi.update(id, {
          name,
          description: templateEditForm.description?.trim() || undefined,
          ownerUserIds,
          milestones: milestonesPayload,
        });
        message.success('保存成功');
        await loadTemplates();
        setEditingTemplateId(null);
        setTemplateEditForm({});
        setTemplateMilestones([]);
        setEditFormOwners([]);
        setIsAddingTemplate(false);
        setTempSelectedMilestoneIds([]);
        setTemplateRelationDirectionDrafts({});
        setTemplateDragState({ dragId: null, overId: null });
      }
    } catch (e) {
      console.error('保存模板失败', e);
      message.error(e instanceof Error ? e.message : '保存失败');
    } finally {
      setTemplateSaveLoading(false);
    }
  };

  const handleDeleteTemplate = async (id: number) => {
    const template = templates.find(t => t.id === id);
    if (!template || !canMutateTemplate(template)) return;
    try {
      await templateApi.delete(id);
      message.success('删除成功');
      await loadTemplates();
    } catch (e) {
      console.error('删除模板失败', e);
      message.error(e instanceof Error ? e.message : '删除失败');
    }
  };

  const confirmDeleteTemplate = (template: MilestoneTemplateVO) => {
    if (!canMutateTemplate(template)) return;
    ipcConfirm({
      title: '确认删除模板？',
      content: `删除后模板「${template.name ?? '-'}」将无法恢复。是否继续？`,
      okText: '确认删除',
      cancelText: '取消',
      danger: true,
      onOk: () => handleDeleteTemplate(template.id),
    });
  };

  const handleCancelTemplate = () => {
    setEditingTemplateId(null);
    setTemplateEditForm({});
    setTemplateMilestones([]);
    setEditFormOwners([]);
    setIsAddingTemplate(false);
    setTempSelectedMilestoneIds([]);
    setTemplateRelationDirectionDrafts({});
    setTemplateDragState({ dragId: null, overId: null });
  };

  const removeMilestoneFromTemplate = (milestoneId: number) =>
    setTemplateMilestones(templateMilestones.filter(m => Number(m.milestoneId) !== milestoneId));

  const updateTemplateMilestone = (milestoneId: number, updates: Partial<TemplateMilestoneConfig>) =>
    setTemplateMilestones(templateMilestones.map(m => Number(m.milestoneId) === milestoneId ? { ...m, ...updates } : m));

  const moveMilestoneByDrag = (dragMilestoneId: number, targetMilestoneId: number) => {
    if (dragMilestoneId === targetMilestoneId) return;
    setTemplateMilestones(prev => {
      const items = [...prev].sort((a, b) => a.order - b.order);
      const fromIndex = items.findIndex(item => Number(item.milestoneId) === dragMilestoneId);
      const toIndex = items.findIndex(item => Number(item.milestoneId) === targetMilestoneId);
      if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return prev;
      const [removed] = items.splice(fromIndex, 1);
      items.splice(toIndex, 0, removed);
      return items.map((m, i) => ({ ...m, order: i + 1 }));
    });
  };

  /** 可选前序节点：当前节点之前的、已加入模板的节点（来自 nodeConfigs） */
  const getAvailableParentMilestones = (currentMilestoneId: number): MilestoneNodeConfig[] => {
    const idx = templateMilestones.findIndex(tm => Number(tm.milestoneId) === currentMilestoneId);
    if (idx <= 0) return [];
    return templateMilestones.slice(0, idx)
      .map(tm => nodeConfigs.find(n => Number(n.id) === Number(tm.milestoneId)))
      .filter((m): m is MilestoneNodeConfig => m != null);
  };

  /** 可选后续节点：当前节点之后的、已加入模板的节点（来自 nodeConfigs） */
  const getAvailableNextMilestones = (currentMilestoneId: number): MilestoneNodeConfig[] => {
    const idx = templateMilestones.findIndex(tm => Number(tm.milestoneId) === currentMilestoneId);
    if (idx < 0 || idx >= templateMilestones.length - 1) return [];
    return templateMilestones
      .slice(idx + 1)
      .map(tm => nodeConfigs.find(n => Number(n.id) === Number(tm.milestoneId)))
      .filter((m): m is MilestoneNodeConfig => m != null);
  };

  const hasTemplateMilestoneValue = (value: TemplateMilestoneConfig['parentMilestoneId'] | TemplateMilestoneConfig['nextMilestoneId']) =>
    value != null && String(value).trim() !== '';

  const getTemplateDependencyDirection = (tm: TemplateMilestoneConfig): TemplateDependencyDirection => {
    const mid = Number(tm.milestoneId);
    if (hasTemplateMilestoneValue(tm.nextMilestoneId)) return 'next';
    if (hasTemplateMilestoneValue(tm.parentMilestoneId)) return 'parent';
    if (templateRelationDirectionDrafts[mid]) return templateRelationDirectionDrafts[mid];
    const parentOptions = getAvailableParentMilestones(mid);
    const nextOptions = getAvailableNextMilestones(mid);
    if (parentOptions.length === 0 && nextOptions.length > 0) return 'next';
    return 'parent';
  };

  const getTemplateMilestoneStatus = (
    tm: TemplateMilestoneConfig,
  ): { status: TemplateMilestoneStatus; label: string; message?: string } => {
    if (tm.timeMethod !== 'auto') return { status: 'manual', label: '手动' };

    const hasParent = hasTemplateMilestoneValue(tm.parentMilestoneId);
    const hasNext = hasTemplateMilestoneValue(tm.nextMilestoneId);
    const dependencyId = tm.parentMilestoneId ?? tm.nextMilestoneId;
    const dependencyExists = dependencyId != null
      && templateMilestones.some(item => Number(item.milestoneId) === Number(dependencyId));
    const hasDuration = tm.durationDays != null && Number(tm.durationDays) > 0;

    if (!(hasParent !== hasNext)) {
      return { status: 'pending', label: '待完善', message: '自动模式下请选择一个依赖方向和节点' };
    }

    if (!dependencyExists) {
      return { status: 'pending', label: '待完善', message: '依赖节点不存在或已不在当前模板中' };
    }

    if (!hasDuration) {
      return { status: 'pending', label: '待完善', message: '自动模式下请填写工期' };
    }

    return { status: 'configured', label: '已配置' };
  };

  const handleTemplateTimeMethodChange = (tm: TemplateMilestoneConfig, value: MilestoneTimeMethod) => {
    const mid = Number(tm.milestoneId);
    if (value === 'manual') {
      setTemplateRelationDirectionDrafts(prev => {
        const next = { ...prev };
        delete next[mid];
        return next;
      });
      updateTemplateMilestone(mid, {
        timeMethod: value,
        parentMilestoneId: undefined,
        nextMilestoneId: undefined,
        durationDays: undefined,
        timeOffsetDays: 1,
        excludeWeekends: undefined,
      });
      return;
    }

    const parentOptions = getAvailableParentMilestones(mid);
    const nextOptions = getAvailableNextMilestones(mid);
    const direction: TemplateDependencyDirection = parentOptions.length === 0 && nextOptions.length > 0 ? 'next' : 'parent';
    setTemplateRelationDirectionDrafts(prev => ({ ...prev, [mid]: direction }));
    updateTemplateMilestone(mid, {
      timeMethod: value,
      parentMilestoneId: direction === 'parent' ? tm.parentMilestoneId : undefined,
      nextMilestoneId: direction === 'next' ? tm.nextMilestoneId : undefined,
      timeOffsetDays: tm.timeOffsetDays ?? 1,
    });
  };

  const handleTemplateDirectionChange = (tm: TemplateMilestoneConfig, direction: TemplateDependencyDirection) => {
    const mid = Number(tm.milestoneId);
    setTemplateRelationDirectionDrafts(prev => ({ ...prev, [mid]: direction }));
    updateTemplateMilestone(mid, {
      parentMilestoneId: undefined,
      nextMilestoneId: undefined,
    });
  };

  const sortedTemplateMilestones = useMemo(
    () => [...templateMilestones].sort((a, b) => a.order - b.order),
    [templateMilestones],
  );

  const templateModalMilestoneColumns = useMemo<ColumnsType<TemplateMilestoneConfig>>(() => [
    {
      title: '',
      width: 48,
      fixed: 'left',
      className: 'template-modal-order-cell',
      render: (_, tm) => (
        <button
          type="button"
          className="template-modal-drag-handle"
          draggable
          aria-label="拖拽调整顺序"
          title="拖拽调整顺序"
          onDragStart={event => {
            const milestoneId = Number(tm.milestoneId);
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', String(milestoneId));
            setTemplateDragState({ dragId: milestoneId, overId: null });
          }}
          onDragEnd={() => setTemplateDragState({ dragId: null, overId: null })}
        >
          <HolderOutlined />
        </button>
      ),
    },
    {
      title: '节点信息',
      width: 180,
      fixed: 'left',
      render: (_, tm) => {
        const node = nodeConfigs.find(n => Number(n.id) === Number(tm.milestoneId));
        return (
          <div>
            <div className="template-modal-node-name">{node?.name ?? `节点${tm.milestoneId}`}</div>
            <div className="template-modal-node-meta">{node?.phase || '-'}</div>
          </div>
        );
      },
    },
    {
      title: '时间方式',
      width: 104,
      render: (_, tm) => (
        <Select
          value={tm.timeMethod}
          onChange={(value: MilestoneTimeMethod) => handleTemplateTimeMethodChange(tm, value)}
          options={[
            { value: 'manual', label: '手动' },
            { value: 'auto', label: '自动' },
          ]}
          getPopupContainer={() => document.body}
        />
      ),
    },
    {
      title: '依赖方向',
      width: 124,
      render: (_, tm) => {
        const mid = Number(tm.milestoneId);
        const parentOptions = getAvailableParentMilestones(mid);
        const nextOptions = getAvailableNextMilestones(mid);
        return tm.timeMethod === 'auto' ? (
          <Select
            value={getTemplateDependencyDirection(tm)}
            onChange={(value: TemplateDependencyDirection) => handleTemplateDirectionChange(tm, value)}
            options={[
              { value: 'parent', label: '前序节点', disabled: parentOptions.length === 0 },
              { value: 'next', label: '后序节点', disabled: nextOptions.length === 0 },
            ]}
            getPopupContainer={() => document.body}
          />
        ) : (
          <span className="template-modal-muted">-</span>
        );
      },
    },
    {
      title: '依赖节点',
      width: 190,
      render: (_, tm) => {
        const mid = Number(tm.milestoneId);
        const direction = getTemplateDependencyDirection(tm);
        const options = direction === 'parent' ? getAvailableParentMilestones(mid) : getAvailableNextMilestones(mid);
        const value = direction === 'parent' ? tm.parentMilestoneId : tm.nextMilestoneId;
        return tm.timeMethod === 'auto' ? (
          <Select
            value={value != null ? Number(value) : undefined}
            onChange={(nextValue?: number) => updateTemplateMilestone(mid, {
              parentMilestoneId: direction === 'parent' ? nextValue : undefined,
              nextMilestoneId: direction === 'next' ? nextValue : undefined,
            })}
            placeholder={options.length > 0 ? '选择依赖节点' : '暂无可用节点'}
            allowClear
            disabled={options.length === 0}
            options={options.map(option => ({ value: Number(option.id), label: option.name }))}
            getPopupContainer={() => document.body}
          />
        ) : (
          <span className="template-modal-muted">表格中维护日期</span>
        );
      },
    },
    {
      title: '工期 / 偏移',
      width: 184,
      render: (_, tm) => {
        const mid = Number(tm.milestoneId);
        const isAuto = tm.timeMethod === 'auto';
        return (
          <div className="template-modal-number-pair">
            <InputNumber
              min={1}
              precision={0}
              value={tm.durationDays ?? undefined}
              onChange={value => updateTemplateMilestone(mid, { durationDays: typeof value === 'number' ? value : undefined })}
              placeholder="工期"
              disabled={!isAuto}
            />
            <InputNumber
              precision={0}
              value={tm.timeOffsetDays ?? 1}
              onChange={value => updateTemplateMilestone(mid, { timeOffsetDays: typeof value === 'number' ? value : 1 })}
              placeholder="偏移"
              disabled={!isAuto}
            />
          </div>
        );
      },
    },
    {
      title: '选项',
      width: 170,
      render: (_, tm) => {
        const mid = Number(tm.milestoneId);
        const isAuto = tm.timeMethod === 'auto';
        return (
          <div className="template-modal-switch-list">
            <label>
              <Switch
                className="template-modal-switch"
                size="small"
                checked={tm.mergeAcrossSpecs || false}
                onChange={checked => updateTemplateMilestone(mid, { mergeAcrossSpecs: checked })}
              />
              <span>跨规格合并</span>
            </label>
            <label>
              <Switch
                className="template-modal-switch"
                size="small"
                checked={tm.excludeWeekends || false}
                disabled={!isAuto}
                onChange={checked => updateTemplateMilestone(mid, { excludeWeekends: checked })}
              />
              <span>排除周末</span>
            </label>
          </div>
        );
      },
    },
    {
      title: '操作',
      width: 78,
      fixed: 'right',
      render: (_, tm) => (
        <IpcRowActions
          actions={[
            { key: 'remove', label: '删除', danger: true, onClick: () => removeMilestoneFromTemplate(Number(tm.milestoneId)) },
          ]}
        />
      ),
    },
  ], [nodeConfigs, templateMilestones, templateRelationDirectionDrafts]);

  // ==================== 节点配置管理（对接接口） ====================

  const handleAddNode = () => {
    if (!canMutateMilestone) return;
    const defaultPhase = milestonePhaseOptions()[0]?.value || '';
    setNodeEditForm({
      name: '',
      phase: defaultPhase,
      defaultLeadDays: 7,
      defaultManDays: 0,
      defaultCost: 0,
      completionMethod: 'manual',
      isFinalNode: false,
      autoCompleteRuleId: undefined,
      description: '',
      type: 'other',
    });
    setIsAddingNode(true);
    setShowNodeModal(true);
  };

  const handleEditNode = (node: MilestoneNodeConfig) => {
    if (!canMutateMilestone) return;
    setNodeEditForm({ ...node });
    setIsAddingNode(false);
    setShowNodeModal(true);
  };

  const handleSaveNode = async () => {
    if (!canMutateMilestone) return;
    const name = nodeEditForm.name?.trim();
    if (!name) {
      message.warning('请填写节点名称');
      return;
    }
    const phase = (nodeEditForm.phase || '').trim();
    if (!phase) {
      message.warning('请选择所属阶段');
      return;
    }
    const defaultLeadDays = nodeEditForm.defaultLeadDays ?? 7;
    const defaultManDays = Math.max(0, Number(nodeEditForm.defaultManDays ?? 0));
    const defaultCost = Math.max(0, Number(nodeEditForm.defaultCost ?? 0));
    const completionMethod = nodeEditForm.completionMethod === 'auto' ? 'auto' : 'manual';
    const isFinalNode = !!nodeEditForm.isFinalNode;
    const autoCompleteRuleId = completionMethod === 'auto'
      ? (nodeEditForm.autoCompleteRuleId ?? '').trim()
      : '';
    if (completionMethod === 'auto' && !autoCompleteRuleId) {
      message.warning('请选择自动完成规则');
      return;
    }
    const ruleIdNum = Number(autoCompleteRuleId);
    const description = nodeEditForm.description?.trim() ?? '';
    const type = nodeEditForm.type ?? 'other';

    setNodeSaveLoading(true);
    try {
      if (isAddingNode) {
        await milestoneNodeApi.create({
          name,
          phase,
          defaultLeadDays: defaultLeadDays < 1 ? 1 : defaultLeadDays,
          defaultManDays,
          defaultCost,
          completionMethod,
          isFinalNode,
          autoCompleteRuleId: ruleIdNum > 0 ? ruleIdNum : undefined,
          description: description || undefined,
          type,
        });
        message.success('新增成功');
      } else {
        const id = nodeEditForm.id;
        if (!id) return;
        await milestoneNodeApi.update(Number(id), {
          name,
          phase,
          defaultLeadDays: defaultLeadDays < 1 ? 1 : defaultLeadDays,
          defaultManDays,
          defaultCost,
          completionMethod,
          isFinalNode,
          autoCompleteRuleId: ruleIdNum > 0 ? ruleIdNum : undefined,
          description: description || undefined,
          type,
        });
        message.success('保存成功');
      }
      setShowNodeModal(false);
      setNodeEditForm({});
      setIsAddingNode(false);
      await loadNodeConfigs();
    } catch (e) {
      console.error('保存节点失败', e);
      message.error(e instanceof Error ? e.message : '保存失败');
    } finally {
      setNodeSaveLoading(false);
    }
  };

  const handleDeleteNode = async (id: string) => {
    if (!canMutateMilestone) return;
    try {
      await milestoneNodeApi.delete(Number(id));
      message.success('删除成功');
      await loadNodeConfigs();
    } catch (e) {
      console.error('删除节点失败', e);
      message.error(e instanceof Error ? e.message : '删除失败');
    }
  };

  const confirmDeleteNode = (node: MilestoneNodeConfig) => {
    if (!canMutateMilestone) return;
    ipcConfirm({
      title: '确认删除节点？',
      content: `删除后节点「${node.name || '-'}」将无法恢复。是否继续？`,
      okText: '确认删除',
      cancelText: '取消',
      danger: true,
      onOk: () => handleDeleteNode(node.id),
    });
  };

  // 阶段列表来自 erpa_base_tbl / IPC_MILESTONE_PHASE，禁止前端写死
  const phaseOptions = useMemo(() => milestonePhaseOptions(), [enumsReady]);
  const phases = useMemo(() => phaseOptions.map(p => p.value), [phaseOptions]);

  const nodePhaseFilterOptions = useMemo(() => {
    const set = new Set<string>(phases);
    for (const n of nodeConfigs) {
      const p = (n.phase || '').trim();
      if (p) set.add(p);
    }
    // 枚举顺序优先，其余（历史脏数据）追加在后
    const ordered = [...phases];
    for (const p of set) {
      if (!ordered.includes(p)) ordered.push(p);
    }
    return ordered.map(p => ({ value: p, label: p }));
  }, [nodeConfigs, phases]);

  const getPhaseTagClassName = (phase?: string) => {
    const index = phases.indexOf(phase || '');
    return `milestone-phase-tag milestone-phase-tag--${index >= 0 ? index : 'other'}`;
  };

  const filteredNodeConfigs = useMemo(() => {
    const keyword = appliedNodeFilterName.trim().toLowerCase();
    return nodeConfigs.filter(node => {
      if (keyword && !(node.name || '').toLowerCase().includes(keyword)) return false;
      if (appliedNodeFilterPhases.length > 0 && !appliedNodeFilterPhases.includes(node.phase || '')) {
        return false;
      }
      if (appliedNodeFilterCompletionMethods.length > 0) {
        const method = node.completionMethod === 'auto' ? 'auto' : 'manual';
        if (!appliedNodeFilterCompletionMethods.includes(method)) return false;
      }
      if (appliedNodeFilterFinalNode === '1' && !node.isFinalNode) return false;
      if (appliedNodeFilterFinalNode === '0' && node.isFinalNode) return false;
      return true;
    });
  }, [
    nodeConfigs,
    appliedNodeFilterName,
    appliedNodeFilterPhases,
    appliedNodeFilterCompletionMethods,
    appliedNodeFilterFinalNode,
  ]);

  const sortedNodeConfigs = [...filteredNodeConfigs].sort((a, b) => {
    const phaseOrderA = phases.indexOf(a.phase);
    const phaseOrderB = phases.indexOf(b.phase);
    const normalizedPhaseA = phaseOrderA === -1 ? phases.length : phaseOrderA;
    const normalizedPhaseB = phaseOrderB === -1 ? phases.length : phaseOrderB;
    if (normalizedPhaseA !== normalizedPhaseB) return normalizedPhaseA - normalizedPhaseB;
    if (a.defaultLeadDays !== b.defaultLeadDays) return a.defaultLeadDays - b.defaultLeadDays;
    return a.name.localeCompare(b.name, 'zh-CN');
  });

  const nodeTotalPages = Math.max(1, Math.ceil(sortedNodeConfigs.length / nodePageSize) || 1);
  const safeNodePage = Math.min(Math.max(1, nodePage), nodeTotalPages);

  useEffect(() => {
    if (sortedNodeConfigs.length === 0) return;
    if (nodePage > nodeTotalPages) {
      setNodePagination(nodeTotalPages, nodePageSize);
    }
  }, [sortedNodeConfigs.length, nodePage, nodePageSize, nodeTotalPages, setNodePagination]);
  const pagedNodeConfigs = sortedNodeConfigs.slice(
    (safeNodePage - 1) * nodePageSize,
    safeNodePage * nodePageSize,
  );

  const templateTableColumns = useMemo<ColumnsType<MilestoneTemplateVO>>(() => {
    const columns: ColumnsType<MilestoneTemplateVO> = [
      {
        title: '模板名称',
        dataIndex: 'name',
        width: 280,
        render: (_, template) => (
          <>
            <div className="template-table-name">{template.name ?? '-'}</div>
            {(template.description ?? '') && (
              <div className="template-table-desc">{template.description}</div>
            )}
          </>
        ),
      },
      {
        title: 'Owner',
        key: 'owners',
        width: 260,
        render: (_, template) => {
          const canEditTemplate = canMutateTemplate(template);
          return renderOwnerRow(getTemplateOwners(template), {
            showAdd: canEditTemplate,
            onAdd: () => openOwnerSelector('list', template),
            removable: canEditTemplate,
            onRemove: userId => handleRemoveOwnerFromList(template, userId),
            hideLabel: true,
          });
        },
      },
      {
        title: '节点数',
        key: 'nodeCount',
        width: 90,
        align: 'center',
        render: (_, template) => (
          <span className="template-table-node-count">{getTemplateNodeCount(template)}</span>
        ),
      },
      {
        title: '创建时间',
        dataIndex: 'create_time',
        width: 130,
        render: value => formatConfigDate(value),
      },
      {
        title: '更新时间',
        dataIndex: 'update_time',
        width: 130,
        render: value => formatConfigDate(value),
      },
    ];

    if (canMutateMilestone) {
      columns.push({
        title: '操作',
        key: 'actions',
        width: 120,
        fixed: 'right',
        className: 'milestone-config-action-cell',
        onHeaderCell: () => ({ className: 'milestone-config-action-header' }),
        render: (_, template) => {
          const canEditTemplate = canMutateTemplate(template);
          return renderMutateButton(
            template,
            canEditTemplate ? (
              <IpcRowActions
                actions={[
                  { key: 'edit', label: '编辑', onClick: () => handleEditTemplate(template) },
                  { key: 'delete', label: '删除', danger: true, onClick: () => confirmDeleteTemplate(template) },
                ]}
              />
            ) : (
              <span className="template-action-disabled">仅 owner 可操作</span>
            )
          );
        },
      });
    }

    return columns;
  }, [
    canMutateMilestone,
    canMutateTemplate,
    confirmDeleteTemplate,
    handleEditTemplate,
    renderMutateButton,
    renderOwnerRow,
  ]);

  const nodeTableColumns = useMemo<ColumnsType<MilestoneNodeConfig>>(() => {
    const columns: ColumnsType<MilestoneNodeConfig> = [
      {
        title: '节点ID',
        key: 'milestoneId',
        width: 300,
        render: (_, node) => {
          const nodeDisplayId = node.milestoneId || node.id;
          return <span className="node-table-id" title={nodeDisplayId}>{nodeDisplayId}</span>;
        },
      },
      {
        title: '节点名称',
        dataIndex: 'name',
        width: 220,
        render: value => <span className="node-table-name" title={value || undefined}>{value || '-'}</span>,
      },
      {
        title: '阶段',
        dataIndex: 'phase',
        width: 120,
        align: 'center',
        render: phase => phase ? <IpcTag variant="outline" className={getPhaseTagClassName(phase)}>{phase}</IpcTag> : '-',
      },
      {
        title: '默认工期(天)',
        dataIndex: 'defaultLeadDays',
        width: 120,
        align: 'center',
        render: value => value ?? '-',
      },
      {
        title: '默认工时(人天)',
        dataIndex: 'defaultManDays',
        width: 120,
        align: 'center',
        render: value => (value == null ? '-' : value),
      },
      {
        title: '默认费用(元)',
        dataIndex: 'defaultCost',
        width: 120,
        align: 'center',
        render: value => (value == null ? '-' : value),
      },
      {
        title: '完成方式',
        dataIndex: 'completionMethod',
        width: 110,
        align: 'center',
        render: value => nodeCompletionMethodLabel(value === 'auto' ? 'auto' : 'manual'),
      },
      {
        title: '最终节点',
        dataIndex: 'isFinalNode',
        width: 90,
        align: 'center',
        render: value => (value ? '是' : '否'),
      },
      {
        title: '描述',
        dataIndex: 'description',
        width: 300,
        render: value => <span className="node-table-desc">{value || '-'}</span>,
      },
    ];

    if (canMutateMilestone) {
      columns.push({
        title: '操作',
        key: 'actions',
        width: 96,
        fixed: 'right',
        className: 'milestone-config-action-cell',
        onHeaderCell: () => ({ className: 'milestone-config-action-header' }),
        render: (_, node) => (
          <IpcRowActions
            actions={[
              { key: 'edit', label: '编辑', onClick: () => handleEditNode(node) },
              { key: 'delete', label: '删除', danger: true, onClick: () => confirmDeleteNode(node) },
            ]}
          />
        ),
      });
    }

    return columns;
  }, [canMutateMilestone, confirmDeleteNode, getPhaseTagClassName, handleEditNode]);

  const templateAppliedChips = [
    appliedFilterName ? { key: 'name', label: `模板: ${appliedFilterName}` } : null,
    ...appliedFilterOwnerIds.map(id => {
      const label = ownerFilterOptions.find(o => o.value === id)?.label ?? String(id);
      return { key: `owner:${id}`, label: `Owner: ${label}` };
    }),
  ].filter((item): item is { key: string; label: string } => item != null);

  const removeTemplateAppliedChip = (key: string) => {
    if (key === 'name') {
      patchTemplateApplied({ filterName: '' });
      return;
    }
    if (key.startsWith('owner:')) {
      const id = Number(key.slice('owner:'.length));
      patchTemplateApplied({
        filterOwnerIds: appliedFilterOwnerIds.filter(x => x !== id),
      });
    }
  };

  const nodeAppliedChips = [
    appliedNodeFilterName ? { key: 'name', label: `节点: ${appliedNodeFilterName}` } : null,
    ...appliedNodeFilterPhases.map(p => ({ key: `phase:${p}`, label: `阶段: ${p}` })),
    ...appliedNodeFilterCompletionMethods.map(m => ({
      key: `completion:${m}`,
      label: `完成方式: ${nodeCompletionMethodLabel(m)}`,
    })),
    appliedNodeFilterFinalNode === '1'
      ? { key: 'final:1', label: '最终节点: 是' }
      : appliedNodeFilterFinalNode === '0'
        ? { key: 'final:0', label: '最终节点: 否' }
        : null,
  ].filter((item): item is { key: string; label: string } => item != null);

  const removeNodeAppliedChip = (key: string) => {
    if (key === 'name') {
      patchNodeApplied({ nodeFilterName: '' });
      return;
    }
    if (key.startsWith('phase:')) {
      const phase = key.slice('phase:'.length);
      patchNodeApplied({
        nodeFilterPhases: appliedNodeFilterPhases.filter(p => p !== phase),
      });
      return;
    }
    if (key.startsWith('completion:')) {
      const method = key.slice('completion:'.length);
      patchNodeApplied({
        nodeFilterCompletionMethods: appliedNodeFilterCompletionMethods.filter(m => m !== method),
      });
      return;
    }
    if (key.startsWith('final:')) {
      patchNodeApplied({ nodeFilterFinalNode: '' });
    }
  };

  // ==================== 渲染 ====================

  const templatesTab = (
    <div className="config-content template-config-content">
      <div className="ipc-table-viewport milestone-template-viewport">
        <div className="ipc-table-viewport__top">
      {isReq022Preview && (
        <div className="preview-notice">
          CP3 预览模式：当前显示 REQ-022 演示数据，可直接验证模板名称筛选、Owner 筛选和 Owner 候选过滤。
        </div>
      )}

      {/* REQ-022: 筛选栏 — 里程碑名字 + Owner */}
      <IpcFilterToolbar
        className="milestone-filter-toolbar"
        onSubmit={applyTemplateIfChanged}
        onReset={resetTemplateQuery}
        chipItems={templateAppliedChips}
        onRemoveChip={removeTemplateAppliedChip}
        onClearChips={resetTemplateQuery}
        extra={canMutateMilestone ? (
          <IpcButton variant="primary" icon={<PlusOutlined />} onClick={handleAddTemplate}>
            新建模板
          </IpcButton>
        ) : null}
      >
        <Input
          className="ipc-filter-bar__search"
          placeholder="搜索模板名称"
          value={filterName}
          onChange={e => patchTemplateDraft({ filterName: e.target.value })}
          onPressEnter={() => applyTemplateIfChanged()}
          allowClear
        />
        <Select
          className="ipc-filter-bar__field milestone-filter-owner"
          mode="multiple"
          placeholder="按 Owner 筛选"
          allowClear
          {...IPC_FILTER_MULTI_SELECT_PROPS}
          value={filterOwnerIds}
          onChange={v => patchAndApplyTemplate({ filterOwnerIds: v ?? [] })}
          options={ownerFilterOptions}
          getPopupContainer={() => document.body}
        />

      </IpcFilterToolbar>
        </div>

      <IpcModal
        className="template-edit-modal"
        title={isAddingTemplate ? '新建里程碑模板' : '编辑里程碑模板'}
        open={showTemplateEditForm}
        onOk={handleSaveTemplate}
        onCancel={handleCancelTemplate}
        okText="保存模板"
        cancelText="取消"
        confirmLoading={templateSaveLoading}
        okDisabled={!templateEditForm.name?.trim()}
        width={1180}
      >
        <div className="template-modal-form">
          <div className="template-modal-form-grid">
            <IpcFormField label="模板名称" required>
              <Input
                value={templateEditForm.name || ''}
                onChange={e => setTemplateEditForm({ ...templateEditForm, name: e.target.value })}
                placeholder="请输入模板名称"
              />
            </IpcFormField>
            <IpcFormField label="模板描述">
              <Input
                value={templateEditForm.description || ''}
                onChange={e => setTemplateEditForm({ ...templateEditForm, description: e.target.value })}
                placeholder="请输入模板描述"
              />
            </IpcFormField>
          </div>

          <div className="template-modal-owner">
            {renderOwnerRow(editFormOwners, {
              showAdd: true,
              onAdd: () => openOwnerSelector('edit'),
              removable: true,
              onRemove: handleRemoveOwnerFromEdit,
            })}
          </div>

          <div className="template-modal-section">
            <div className="template-modal-section-header">
              <h4>里程碑节点配置</h4>
              <IpcButton size="small" variant="secondary" icon={<PlusOutlined />} onClick={handleOpenMilestoneSelector}>
                添加节点
              </IpcButton>
            </div>

            <IpcTable<TemplateMilestoneConfig>
              compact
              frameless
              showHeaderIcon={false}
              panelClassName="template-modal-table-panel"
              scrollClassName="template-modal-table-wrap"
              className="template-modal-ipc-table"
              rowKey={record => String(record.milestoneId)}
              columns={templateModalMilestoneColumns}
              dataSource={sortedTemplateMilestones}
              pagination={false}
              scroll={{ x: 1078, y: 336 }}
              rowClassName={record => [
                templateDragState.dragId === Number(record.milestoneId) ? 'template-modal-row--dragging' : '',
                templateDragState.overId === Number(record.milestoneId)
                  && templateDragState.dragId !== Number(record.milestoneId)
                  ? 'template-modal-row--drop-target'
                  : '',
              ].filter(Boolean).join(' ')}
              onRow={record => ({
                onDragOver: event => {
                  if (templateDragState.dragId == null) return;
                  event.preventDefault();
                  event.dataTransfer.dropEffect = 'move';
                  const overId = Number(record.milestoneId);
                  if (templateDragState.overId !== overId) {
                    setTemplateDragState(prev => ({ ...prev, overId }));
                  }
                },
                onDrop: event => {
                  event.preventDefault();
                  const dragId = Number(event.dataTransfer.getData('text/plain') || templateDragState.dragId);
                  const targetId = Number(record.milestoneId);
                  if (Number.isFinite(dragId) && Number.isFinite(targetId)) {
                    moveMilestoneByDrag(dragId, targetId);
                  }
                  setTemplateDragState({ dragId: null, overId: null });
                },
              })}
              emptyTitle="请点击「添加节点」配置里程碑节点"
              emptyDescription=""
            />
            <div className="template-modal-tip">提示：使用左侧按钮调整节点顺序。偏移天数相对于上一节点结束日期计算。</div>
          </div>
        </div>
      </IpcModal>

      {/* 节点选择弹窗 */}
      <IpcModal
        className="milestone-selector-modal"
        title="选择里程碑节点"
        open={showMilestoneSelector && canEditCurrentTemplate}
        onOk={handleSaveMilestoneSelection}
        onCancel={() => setShowMilestoneSelector(false)}
        okText="保存配置"
        cancelText="取消"
        width={480}
      >
        <p className="config-modal-hint">请选择要包含在模板中的里程碑节点：</p>
        <div className="milestone-checkbox-list">
          {nodeConfigs.map(node => (
            <label key={node.id} className="milestone-checkbox-item">
              <Checkbox
                checked={tempSelectedMilestoneIds.includes(Number(node.id))}
                onChange={e => {
                  const id = Number(node.id);
                  setTempSelectedMilestoneIds(e.target.checked
                    ? [...tempSelectedMilestoneIds, id]
                    : tempSelectedMilestoneIds.filter(i => i !== id)
                  );
                }}
              />
              <span className="milestone-checkbox-name">{node.name}</span>
              <IpcTag variant="outline" className={getPhaseTagClassName(node.phase)}>{node.phase}</IpcTag>
            </label>
          ))}
        </div>
      </IpcModal>

      {/* Owner 搜索选择器弹窗 */}
      <IpcModal
        className="owner-selector-modal"
        title="添加 Owner"
        open={showOwnerSelector}
        onOk={handleConfirmAddOwners}
        onCancel={() => setShowOwnerSelector(false)}
        okText="确认添加"
        cancelText="取消"
        confirmLoading={ownerSaveLoading}
        width={480}
      >
        <p className="config-modal-hint">搜索并选择要添加为 owner 的用户（可多选，仅限可编辑或系统管理权限）：</p>
        <Input
          value={ownerSearchKeyword}
          onChange={e => setOwnerSearchKeyword(e.target.value)}
          placeholder="输入姓名搜索"
          allowClear
          className="ipc-search-input"
          style={{ marginBottom: 12 }}
        />
        <div className="owner-search-results">
          {filteredOwnerCandidates.length === 0 ? (
            <IpcEmpty title="未找到匹配用户" description="" />
          ) : (
            filteredOwnerCandidates.map(user => {
              const isExistingOwner = existingOwnerIds.has(user.id);
              const selected = !isExistingOwner && pendingOwnerIds.includes(user.id);
              return (
                <button
                  key={user.id}
                  type="button"
                  disabled={isExistingOwner}
                  className={`owner-search-item${selected ? ' selected' : ''}${isExistingOwner ? ' already-added' : ''}`}
                  onClick={() => {
                    if (isExistingOwner) return;
                    setPendingOwnerIds(prev =>
                      selected ? prev.filter(id => id !== user.id) : [...prev, user.id]
                    );
                  }}
                >
                  <span className="owner-avatar">{getOwnerInitial(user.user_name)}</span>
                  <span className="owner-search-item-name">{user.user_name}</span>
                  {isExistingOwner && <span className="owner-already-badge">已添加</span>}
                </button>
              );
            })
          )}
        </div>
        {pendingOwnerIds.length > 0 && (
          <div className="owner-selected-preview">
            <span className="owner-selected-label">已选：</span>
            {pendingOwnerIds.map(id => {
              const user = allUsers.find(u => u.id === id);
              if (!user) return null;
              return (
                <span key={id} className="owner-tag">
                  <span className="owner-avatar">{getOwnerInitial(user.user_name)}</span>
                  <span className="owner-name">{user.user_name}</span>
                  <button
                    type="button"
                    className="owner-remove-btn"
                    onClick={() => setPendingOwnerIds(prev => prev.filter(x => x !== id))}
                    aria-label="取消选择"
                  >
                    <CloseOutlined style={{ fontSize: 10 }} />
                  </button>
                </span>
              );
            })}
          </div>
        )}
      </IpcModal>

      {/* 模板主表：每个模板一行，节点数由 milestones.length 得出。 */}
        <div className="ipc-table-viewport__table milestone-template-table-area">
      <Spin spinning={templatesLoading} tip="加载中...">
        {filteredTemplates.length > 0 && (
          <IpcTable<MilestoneTemplateVO>
            listViewKey={LIST_VIEW_KEYS.milestoneTemplates}
            compact
            frameless
            resizable
            rowKey={template => template.id}
            columns={templateTableColumns}
            dataSource={pagedTemplates}
            pagination={{
              current: safeTemplatePage,
              pageSize: templatePageSize,
              total: filteredTemplates.length,
              pageSizeOptions: MILESTONE_TABLE_PAGE_SIZE_OPTIONS,
              onChange: (page, pageSize) => {
                setTemplatePagination(page, pageSize);
              },
            }}
            size="small"
            tableLayout="fixed"
            autoHeight
            autoHeightDeps={[filteredTemplates.length, templatePage, templatePageSize, canMutateMilestone]}
            scroll={{ x: canMutateMilestone ? 1050 : 890 }}
            panelClassName="milestone-config-table-panel"
            scrollClassName="milestone-config-table-wrap"
            className="milestone-config-table template-config-table"
          />
        )}

        {filteredTemplates.length === 0 && !templatesLoading && !showTemplateEditForm && (
          <IpcEmpty
            title={
              templates.length === 0
                ? (canMutateMilestone ? '暂无里程碑模板，请点击「新建模板」创建' : (canViewMilestone ? '暂无里程碑模板' : '暂无权限查看里程碑模板'))
                : '筛选条件下无匹配模板，请调整筛选条件'
            }
            description=""
          />
        )}
      </Spin>
        </div>
      </div>
    </div>
  );

  const nodesTab = (
    <div className="config-content template-config-content">
      <div className="ipc-table-viewport milestone-node-viewport">
        <div className="ipc-table-viewport__top">
      {isReq022Preview && (
        <div className="preview-notice">
          CP3 预览模式：当前显示 REQ-022 演示数据，可直接验证模板名称筛选、Owner 筛选和 Owner 候选过滤。
        </div>
      )}

      <IpcFilterToolbar
        className="milestone-filter-toolbar"
        onSubmit={applyNodeIfChanged}
        onReset={resetNodeQuery}
        chipItems={nodeAppliedChips}
        onRemoveChip={removeNodeAppliedChip}
        onClearChips={resetNodeQuery}
        extra={canMutateMilestone ? (
          <IpcButton variant="primary" icon={<PlusOutlined />} onClick={handleAddNode}>
            新增节点
          </IpcButton>
        ) : null}
      >
        <Input
          className="ipc-filter-bar__search"
          placeholder="搜索节点名称"
          value={nodeFilterName}
          onChange={e => patchNodeDraft({ nodeFilterName: e.target.value })}
          onPressEnter={() => applyNodeIfChanged()}
          allowClear
        />
        <Select
          className="ipc-filter-bar__field"
          mode="multiple"
          placeholder="阶段"
          allowClear
          {...IPC_FILTER_MULTI_SELECT_PROPS}
          value={nodeFilterPhases}
          onChange={v => patchAndApplyNode({ nodeFilterPhases: v ?? [] })}
          options={nodePhaseFilterOptions}
          getPopupContainer={() => document.body}
        />
        <Select
          className="ipc-filter-bar__field"
          mode="multiple"
          placeholder="完成方式"
          allowClear
          {...IPC_FILTER_MULTI_SELECT_PROPS}
          value={nodeFilterCompletionMethods}
          onChange={v => patchAndApplyNode({ nodeFilterCompletionMethods: v ?? [] })}
          options={nodeCompletionMethodOptions()}
          getPopupContainer={() => document.body}
        />
        <Select
          className="ipc-filter-bar__field"
          placeholder="最终节点"
          allowClear
          value={nodeFilterFinalNode || undefined}
          onChange={v => patchAndApplyNode({ nodeFilterFinalNode: v ?? '' })}
          options={[
            { value: '1', label: '是' },
            { value: '0', label: '否' },
          ]}
          getPopupContainer={() => document.body}
        />

      </IpcFilterToolbar>
        </div>

        <div className="ipc-table-viewport__table milestone-template-table-area">
      <Spin spinning={nodesLoading} tip="加载中...">
        {sortedNodeConfigs.length > 0 ? (
          <IpcTable<MilestoneNodeConfig>
            listViewKey={LIST_VIEW_KEYS.milestoneNodes}
            compact
            frameless
            resizable
            rowKey={node => node.id}
            columns={nodeTableColumns}
            dataSource={pagedNodeConfigs}
            pagination={{
              current: safeNodePage,
              pageSize: nodePageSize,
              total: sortedNodeConfigs.length,
              pageSizeOptions: MILESTONE_TABLE_PAGE_SIZE_OPTIONS,
              onChange: (page, pageSize) => {
                setNodePagination(page, pageSize);
              },
            }}
            size="small"
            tableLayout="fixed"
            autoHeight
            autoHeightDeps={[sortedNodeConfigs.length, nodePage, nodePageSize, canMutateMilestone]}
            scroll={{ x: canMutateMilestone ? 1136 : 1040 }}
            panelClassName="milestone-config-table-panel"
            scrollClassName="milestone-config-table-wrap"
            className="milestone-config-table node-config-table"
          />
        ) : (
          !nodesLoading && (
            <IpcEmpty
              title={nodeConfigs.length === 0
                ? (canMutateMilestone ? '暂无节点配置，请点击「新增节点」或检查后端服务' : '暂无节点配置')
                : '筛选条件下无匹配节点，请调整筛选条件'
              }
              description=""
            />
          )
        )}
      </Spin>
        </div>
      </div>

      {/* 新增/编辑节点弹窗 */}
      <IpcModal
        title={isAddingNode ? '新增节点' : '编辑节点'}
        open={showNodeModal && canMutateMilestone}
        onOk={handleSaveNode}
        onCancel={() => { setShowNodeModal(false); setNodeEditForm({}); setIsAddingNode(false); }}
        okText="保存"
        cancelText="取消"
        confirmLoading={nodeSaveLoading}
        okDisabled={!nodeEditForm.name?.trim()}
        width={560}
      >
        <div className="node-modal-fields">
          <IpcFormField label="节点名称" required>
            <Input
              value={nodeEditForm.name || ''}
              onChange={e => setNodeEditForm({ ...nodeEditForm, name: e.target.value })}
              placeholder="如：配方确认"
              autoFocus={!isAddingNode}
              maxLength={100}
            />
          </IpcFormField>

          <div className="node-modal-fields__row">
            <IpcFormField label="所属阶段" required>
              <Select
                value={nodeEditForm.phase || undefined}
                onChange={v => setNodeEditForm({ ...nodeEditForm, phase: v })}
                style={{ width: '100%' }}
                options={phaseOptions}
                placeholder={phaseOptions.length ? '请选择阶段' : '阶段枚举未配置'}
                disabled={phaseOptions.length === 0}
                getPopupContainer={() => document.body}
              />
            </IpcFormField>
            <IpcFormField label="默认工期(天)" required>
              <InputNumber
                min={1}
                precision={0}
                value={nodeEditForm.defaultLeadDays ?? 7}
                onChange={v => setNodeEditForm({
                  ...nodeEditForm,
                  defaultLeadDays: typeof v === 'number' && v >= 1 ? v : 7,
                })}
                style={{ width: '100%' }}
                placeholder="请输入默认工期"
              />
            </IpcFormField>
          </div>

          <div className="node-modal-fields__row">
            <IpcFormField label="默认工时(人天)">
              <InputNumber
                min={0}
                precision={2}
                value={nodeEditForm.defaultManDays ?? 0}
                onChange={v => setNodeEditForm({
                  ...nodeEditForm,
                  defaultManDays: typeof v === 'number' && v >= 0 ? v : 0,
                })}
                style={{ width: '100%' }}
                placeholder="人天"
              />
            </IpcFormField>
            <IpcFormField label="默认费用(元)">
              <InputNumber
                min={0}
                precision={2}
                value={nodeEditForm.defaultCost ?? 0}
                onChange={v => setNodeEditForm({
                  ...nodeEditForm,
                  defaultCost: typeof v === 'number' && v >= 0 ? v : 0,
                })}
                style={{ width: '100%' }}
                placeholder="元"
              />
            </IpcFormField>
          </div>

          <IpcFormField label="完成方式" required>
            <Select
              value={nodeEditForm.completionMethod === 'auto' ? 'auto' : 'manual'}
              onChange={v => setNodeEditForm({
                ...nodeEditForm,
                completionMethod: v === 'auto' ? 'auto' : 'manual',
              })}
              style={{ width: '100%' }}
              options={nodeCompletionMethodOptions()}
              getPopupContainer={() => document.body}
            />
            <div className="node-modal-fields__hint">
              {nodeEditForm.completionMethod === 'auto'
                ? `${nodeCompletionMethodLabel('auto')}：按下方配置在条件满足时自动标记节点完成`
                : `${nodeCompletionMethodLabel('manual')}：节点完成后由业务人员手动标记完成状态`}
            </div>
          </IpcFormField>

          {nodeEditForm.completionMethod === 'auto' && (
            <IpcFormField label="自动完成规则" required fullWidth>
              <Select
                value={nodeEditForm.autoCompleteRuleId || undefined}
                onChange={v => setNodeEditForm({
                  ...nodeEditForm,
                  autoCompleteRuleId: v ?? undefined,
                })}
                options={autoCompleteRuleOptions}
                loading={autoCompleteRulesLoading}
                placeholder="请选择自动完成规则"
                allowClear
                showSearch
                optionFilterProp="label"
                notFoundContent="暂无规则，请先在「自动完成规则」页签中配置"
                style={{ width: '100%' }}
                getPopupContainer={() => document.body}
              />
              <div className="node-modal-fields__hint">
                规则在「自动完成规则」页签维护
              </div>
            </IpcFormField>
          )}

          <IpcFormField label="最终节点">
            <Checkbox
              checked={!!nodeEditForm.isFinalNode}
              onChange={e => setNodeEditForm({ ...nodeEditForm, isFinalNode: e.target.checked })}
            >
              标记为最终节点（完成即视为项目「已完成」）
            </Checkbox>
          </IpcFormField>

          <IpcFormField label="节点描述（选填）" fullWidth>
            <TextArea
              value={nodeEditForm.description || ''}
              onChange={e => setNodeEditForm({ ...nodeEditForm, description: e.target.value })}
              placeholder="简要描述该节点的含义"
              rows={3}
              style={{ resize: 'vertical' }}
            />
          </IpcFormField>
        </div>
      </IpcModal>
    </div>
  );

  return (
    <div className="milestone-config ipc-page">
      <div className="config-tabs-wrap">
        <IpcModuleTabs
          activeKey={activeTab}
          onChange={key => {
            if (isMilestoneConfigTabKey(key)) {
              navigate(`/config/${key}`);
            }
          }}
          items={[
            { key: 'templates', label: '里程碑模板', children: templatesTab },
            { key: 'nodes', label: '节点配置', children: nodesTab },
            {
              key: 'autoCompleteRules',
              label: '自动完成规则',
              children: (
                <NodeAutoCompleteRulesTab
                  canMutate={canMutateMilestone}
                  onRulesChanged={loadAutoCompleteRules}
                />
              ),
            },
          ]}
        />
      </div>

    </div>
  );
};

export default MilestoneConfig;
